import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { ReportCardComponentProps } from '../schema-types';
import { filterDOMProps } from '../utils/prop-normalizer';
import { processTextContent } from './text-utils';

export interface AccordionProps extends ReportCardComponentProps {
  title?: string;
  question?: string;
  defaultExpanded?: boolean;
  variant?: 'elevation' | 'outlined';
  disabled?: boolean;
  expandIcon?: React.ReactNode;
  titleVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2';
  titleStyle?: React.CSSProperties;
  detailsStyle?: React.CSSProperties;
  summaryStyle?: React.CSSProperties;
  useQuestionStyle?: boolean;
}

export const AccordionComponent: React.FC<AccordionProps> = ({
  title = 'Section',
  question,
  defaultExpanded = false,
  variant = 'elevation',
  disabled = false,
  expandIcon,
  titleVariant = 'h6',
  titleStyle = {},
  detailsStyle = {},
  summaryStyle = {},
  useQuestionStyle = false,
  children,
  data,
  baseData,
  player,
  playerAssessment,
  reportCardLevel: _reportCardLevel,
  context,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  // Check if we're in debug mode on initial render
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Listen for custom print preparation event
  useEffect(() => {
    const handlePrepareForPrint = () => {
      setExpanded(true);
    };

    window.addEventListener('prepare-for-print', handlePrepareForPrint);
    return () => window.removeEventListener('prepare-for-print', handlePrepareForPrint);
  }, []);

  // Check if we're in print mode and expand all sections
  useEffect(() => {
    const mediaQueryList = window.matchMedia('print');
    const handlePrintChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        // Print mode - expand all sections
        setExpanded(true);
      }
    };

    // Check initial state
    handlePrintChange(mediaQueryList);

    // Listen for changes
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handlePrintChange);
      return () => mediaQueryList.removeEventListener('change', handlePrintChange);
    } else {
      // Fallback for older browsers
      mediaQueryList.addListener(handlePrintChange);
      return () => mediaQueryList.removeListener(handlePrintChange);
    }
  }, []);

  // Also listen for the beforeprint event
  useEffect(() => {
    const handleBeforePrint = () => {
      setExpanded(true);
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    return () => window.removeEventListener('beforeprint', handleBeforePrint);
  }, []);

  const handleChange = (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  // Use data title/question if provided, otherwise use props
  const displayTitle = data?.title || title;
  const displayQuestion = data?.question || question;

  // Determine what to display in the header
  const headerContent = displayQuestion || displayTitle;

  const renderHeader = () => {
    if (useQuestionStyle && displayQuestion) {
      // Responsive question styling
      const iconSize = isMobile ? '48px' : isTablet ? '56px' : '66px';
      const iconLeft = isMobile ? -42 : isTablet ? -48 : -54;
      const iconTop = isMobile ? -2 : isTablet ? -3 : -5;
      const boxMarginLeft = isMobile ? 3 : isTablet ? 4 : 5;
      const textVariant = isMobile ? 'body1' : isTablet ? 'h6' : 'h6';
      const textPadding = isMobile ? '8px 12px' : isTablet ? '10px 14px' : '12px 16px';
      const textSize = isMobile ? '0.9rem' : isTablet ? '1.1rem' : '1.25rem';

      return (
        <Box display="flex" alignItems="center" justifyContent="center" position="relative">
          <Box position="relative" ml={boxMarginLeft}>
            <HelpOutlineIcon
              style={{
                marginRight: 8,
                color: '#000',
                background: '#fff',
                border: 0,
                borderRadius: '50%',
                fontSize: iconSize,
                position: 'absolute',
                top: iconTop,
                left: iconLeft,
                zIndex: 2
              }}
            />
            <Typography
              variant={textVariant}
              style={{
                background: '#6b8e23',
                color: '#fff',
                borderRadius: isMobile ? 6 : 8,
                padding: textPadding,
                fontWeight: 600,
                position: 'relative',
                zIndex: 1,
                fontSize: textSize,
                lineHeight: isMobile ? 1.3 : 1.4,
                ...titleStyle
              }}
            >
              {processTextContent(displayQuestion || '', 0, data, baseData, player, playerAssessment)}
            </Typography>
          </Box>
        </Box>
      );
    } else {
      // Responsive standard accordion header
      const headerVariant = isMobile ? 'body1' : titleVariant;
      const headerFontSize = isMobile ? '1rem' : isTablet ? '1.125rem' : undefined;

      return (
        <Typography
          variant={headerVariant}
          style={{
            fontWeight: 600,
            fontSize: headerFontSize,
            lineHeight: isMobile ? 1.3 : 1.4,
            ...titleStyle
          }}
        >
          {processTextContent(headerContent || '', 0, data, baseData, player, playerAssessment)}
        </Typography>
      );
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      variant={variant}
      disabled={disabled}
      style={{ marginBottom: isMobile ? 12 : isTablet ? 14 : 16 }}
      {...filterDOMProps(props)}
    >
      <AccordionSummary
        expandIcon={expandIcon || <ExpandMoreIcon style={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />}
        style={{
          backgroundColor: useQuestionStyle ? 'transparent' : '#f5f5f5',
          borderBottom: useQuestionStyle ? 'none' : '1px solid #e0e0e0',
          minHeight: useQuestionStyle ? (isMobile ? 60 : isTablet ? 70 : 80) : (isMobile ? 40 : isTablet ? 44 : 48),
          padding: useQuestionStyle
            ? (isMobile ? '6px 12px' : isTablet ? '7px 14px' : '8px 16px')
            : (isMobile ? '0 12px' : '0 16px'),
          ...summaryStyle
        }}
      >
        {renderHeader()}
      </AccordionSummary>
      <AccordionDetails
        style={{
          padding: isMobile ? '12px 16px' : isTablet ? '14px 20px' : '16px 24px',
          flexDirection: 'column',
          paddingTop: useQuestionStyle ? 0 : (isMobile ? 12 : isTablet ? 14 : 16),
          ...detailsStyle
        }}
      >
        <Box>
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
